import React, { lazy, Suspense, useEffect } from "react";
import Sentry from "app/Instrumentation";
import { Redirect } from "react-router-dom";
import { StFlexDiv } from "@mediamath/design-system-eng";
import AccessDeniedError from "core/shared/presenter/AccessDeniedError";
import AdminMessages from "newadmin/intl/AdminMessages";
import AppFeatureFlags from "core/app/util/AppFeatureFlags";
import AppHeader from "core/app/presenter/AppHeader";
import AudienceMessages from "audience/intl/AudienceMessages";
import BulkManagerMessages from "bulkmanager/intl/BulkManagerMessages";
import CampaignMessages from "campaign/intl/CampaignMessages";
import CreativesModuleMessages from "creatives/intl/CreativesModuleMessages";
import EnterpriseControlsMessages from "newadmin/intl/EnterpriseControlsMessages";
import InventoryMessages from "inventory/intl/InventoryMessages";
import Logout from "core/shared/presenter/Logout";
import NavRoutes from "core/app/util/NavRoutes";
import PlanningListMessages from "strategy-plans/intl/PlanningListMessages";
import Route from "core/shared/presenter/Route";
import SectionNameMessages from "core/shared/intl/SectionNameMessages";
import SideNav from "core/app/presenter/sideNav/SideNav";
import SideNavConfig from "core/app/util/SideNavConfig";
import SmartSwitch from "core/shared/util/SmartSwitch";
import { useFeatureToggle } from "@flopflip/react-broadcast";
import StrategyMessages from "campaign/intl/StrategyMessages";
import Wrappers from "app/Wrappers";
import { ErrorPresenter } from "app/ErrorBoundary";

const CAMPAIGNS_ROUTE = NavRoutes.campaigns.default;

// Lazy load modules for better code splitting
const Sidekick = lazy(() => import("sidekick/components/Sidekick"));
const CampaignModule = lazy(() => import("campaign/presenter/CampaignModule"));
const ReportsModule = lazy(() => import("reports/ReportsModule"));
const CreativesModulePresenter = lazy(() =>
  import("creatives/presenter/CreativesModulePresenter")
);
const InventoryModulePresenter = lazy(() =>
  import("inventory/presenter/InventoryModulePresenter")
);
const EnterpriseControlsModule = lazy(() =>
  import("enterprise_controls/presenter/EnterpriseControlsModule")
);
const AdminModule = lazy(() => import("newadmin/presenter/AdminModule"));
const AudienceModule = lazy(() => import("audience/AudienceModule"));
const BulkManagerModule = lazy(() => import("bulkmanager/BulkManagerModule"));
const PlacesModule = lazy(() => import("places/PlacesModule"));
const PlanningModule = lazy(() =>
  import("strategy-plans/presenter/PlanningModule")
);
const Frame = lazy(() => import("core/shared/presenter/Frame"));
const HybridFrame = lazy(() => import("core/shared/presenter/HybridFrame"));
const ExternalPreviewWrapper = lazy(() =>
  import("creatives/presenter/ExternalPreviewWrapper")
);
const PreviewSandboxPage = lazy(() =>
  import("creatives/presenter/preview-sandbox/PreviewSandboxPage")
);
const DisplaySandboxPage = lazy(() =>
  import(
    "creatives/presenter/preview-sandbox/components/UnifiedSandboxPage"
  ).then((module) => {
    const Component = module.default;
    return {
      default: (props) => <Component {...props} contentType="display" />,
    };
  })
);
const NativeSandboxPage = lazy(() =>
  import(
    "creatives/presenter/preview-sandbox/components/UnifiedSandboxPage"
  ).then((module) => {
    const Component = module.default;
    return {
      default: (props) => <Component {...props} contentType="native" />,
    };
  })
);
const LandingPage = () => <Redirect to={CAMPAIGNS_ROUTE} />;

const fallbackDiv = <div />;

const SidekickGate = () => {
  const isEnabled = useFeatureToggle(AppFeatureFlags.SHOW_SIDEKICK);
  if (!isEnabled) return null;
  return (
    <Suspense fallback={fallbackDiv}>
      <Sidekick />
    </Suspense>
  );
};

/* eslint-disable react/prop-types */
const Root = () => {
  useEffect(() => {
    return () => {
      Sentry.close();
    };
  }, []);

  // Render sandbox pages and external preview without app shell for iframe isolation
  // Use window.location directly since this runs before Router is mounted
  const sandboxRoutes = {
    "/creative-preview-sandbox": PreviewSandboxPage,
    "/display-preview-sandbox": DisplaySandboxPage,
    "/native-preview-sandbox": NativeSandboxPage,
  };

  const SandboxComponent = sandboxRoutes[window.location.pathname];
  if (SandboxComponent) {
    return (
      <Suspense fallback={fallbackDiv}>
        <SandboxComponent />
      </Suspense>
    );
  }

  // Handle external preview route without Auth0
  if (window.location.pathname.startsWith("/creative-preview/")) {
    return <ExternalPreviewWrapper />;
  }

  return (
    <Wrappers>
      <AppHeader />
      <StFlexDiv flexFlow="row">
        <SideNav data={SideNavConfig} />
        <Sentry.ErrorBoundary fallback={ErrorPresenter}>
          <Suspense fallback={fallbackDiv}>
            <SmartSwitch>
              <Route auth exact path="/" component={LandingPage} />
              <Route exact path="/logout" component={Logout} />
              <Route
                auth
                path="/admin"
                title={AdminMessages.admin}
                component={AdminModule}
              />
              <Route
                auth
                path="/campaigns"
                flag={AppFeatureFlags.CAMPAIGNS_MODULE}
                title={CampaignMessages.CampaignsHeader}
                component={CampaignModule}
              />
              <Route
                auth
                path="/bulkmanager"
                flag={AppFeatureFlags.NEW_BULKMANAGER_MODULE}
                title={BulkManagerMessages.header}
                component={BulkManagerModule}
              />
              <Route
                auth
                path="/strategies"
                flag={AppFeatureFlags.CAMPAIGNS_MODULE}
                title={StrategyMessages.StrategiesHeader}
                component={CampaignModule}
              />
              <Route
                auth
                path="/strategySearch"
                flag={AppFeatureFlags.CAMPAIGNS_MODULE}
                title={StrategyMessages.StrategySearch}
                component={CampaignModule}
              />
              <Route
                auth
                path="/audiences"
                flag={AppFeatureFlags.AUDIENCE_MODULE}
                title={AudienceMessages.audiencesHeader}
                component={AudienceModule}
              />
              <Route
                auth
                path="/places"
                flag={AppFeatureFlags.PLACES_MODULE}
                title={SectionNameMessages.places}
                component={PlacesModule}
              />
              <Route
                auth
                path="/inventory"
                title={InventoryMessages.DealHeader}
                component={InventoryModulePresenter}
              />
              <Route
                auth
                path="/enterprise-controls"
                flag={AppFeatureFlags.ENTERPRISE_CONTROLS_MODULE}
                title={EnterpriseControlsMessages.EnterpriseControlsHeader}
                component={EnterpriseControlsModule}
              />
              <Route
                auth
                path="/creative"
                flag={AppFeatureFlags.CREATIVES_MODULE}
                title={CreativesModuleMessages.CreativesHeader}
                component={CreativesModulePresenter}
              />
              <Route
                auth
                path="/plans"
                title={PlanningListMessages.planTitle}
                component={PlanningModule}
              />
              <Route
                exact
                path="/access-denied"
                component={AccessDeniedError}
              />
              <Route
                auth
                path="/reports"
                flag={AppFeatureFlags.REPORTS_MODULE}
                title={SectionNameMessages.reports}
                component={ReportsModule}
              />
              {/* Compass Routes with page titles */}
              <Route
                auth
                path="/c/monitor"
                title={CampaignMessages.CampaignsHeader}
                component={Frame}
              />
              <Route
                auth
                path="/c/reports"
                title={SectionNameMessages.reports}
                component={Frame}
              />
              <Route
                auth
                path="/c/media"
                title={InventoryMessages.DealHeader}
                component={Frame}
              />
              <Route
                auth
                path="/c/admin"
                title={AdminMessages.admin}
                component={Frame}
              />
              <Route
                auth
                path="/c/creatives"
                title={CreativesModuleMessages.CreativesHeader}
                component={Frame}
              />
              <Route
                auth
                path="/c/audiences"
                title={AudienceMessages.audiencesHeader}
                component={Frame}
              />
              <Route auth exact path="/c/*" component={Frame} />
              {/* Hybrid Routes with page titles */}
              <Route
                auth
                path="/h/monitor"
                title={CampaignMessages.CampaignsHeader}
                component={HybridFrame}
              />
              <Route
                auth
                path="/h/reports"
                title={SectionNameMessages.reports}
                component={HybridFrame}
              />
              <Route
                auth
                path="/h/media"
                title={InventoryMessages.DealHeader}
                component={HybridFrame}
              />
              <Route
                auth
                path="/h/admin"
                title={AdminMessages.admin}
                component={HybridFrame}
              />
              <Route
                auth
                path="/h/creatives"
                title={CreativesModuleMessages.CreativesHeader}
                component={HybridFrame}
              />
              <Route
                auth
                path="/h/audiences"
                title={AudienceMessages.audiencesHeader}
                component={HybridFrame}
              />
              <Route auth exact path="/h/*" component={HybridFrame} />
            </SmartSwitch>
          </Suspense>
        </Sentry.ErrorBoundary>
      </StFlexDiv>
      <SidekickGate />
    </Wrappers>
  );
};

export default Sentry.withProfiler(Root, { name: "NewT1" });
